import { useMutation, useQuery, useQueryClient } from "react-query";

import axios from "axios";
import { nuclearn_api_url } from "src/App";

const getBaseUrl = () => `${nuclearn_api_url}/resource-lists`;

const getRemoteResources = (activeOnly: boolean) => {
  return axios
    .get(getBaseUrl(), { params: { active_only: activeOnly } })
    .then((response) => {
      return response.data;
    });
};

export const useGetRemoteResources = ({
  activeOnly = true,
}: {
  activeOnly?: boolean;
}) => {
  return useQuery(["resource-list"], () => getRemoteResources(activeOnly), {
    refetchOnWindowFocus: false,
  });
};

const getRemoteResourceById = (id: number) => {
  return axios.get(`${getBaseUrl()}/${id}`).then((response) => {
    return response.data;
  });
};

export const useGetRemoteResourceById = (id: number | null) => {
  return useQuery({
    queryKey: ["resource-list", id],
    queryFn: () => getRemoteResourceById(id as number),
    refetchOnWindowFocus: false,
    enabled: Boolean(id),
  });
};

const updateRemoteResource = (id: number, data: any) => {
  return axios.put(`${getBaseUrl()}/${id}`, data).then((response) => {
    return response.data;
  });
};

export const useUpdateRemoteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => updateRemoteResource(data.id, data),
    onSuccess: () => {
      queryClient.invalidateQueries("resource-list");
      queryClient.invalidateQueries("remoteDatasets");
      queryClient.invalidateQueries("marketplacePersonas");
    },
  });
};

const createRemoteResource = (data: any) => {
  return axios.post(getBaseUrl(), data).then((response) => {
    return response.data;
  });
};

export const useCreateRemoteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: any) => createRemoteResource(data),
    onSuccess: () => {
      queryClient.invalidateQueries("resource-list");
      queryClient.invalidateQueries("remoteDatasets");
      queryClient.invalidateQueries("marketplacePersonas");
    },
  });
};

const deleteRemoteResource = (id: number) => {
  return axios.delete(`${getBaseUrl()}/${id}`).then((response) => {
    return response.data;
  });
};

export const useDeleteRemoteResource = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => deleteRemoteResource(id),
    onSuccess: () => {
      queryClient.invalidateQueries("resource-list");
      queryClient.invalidateQueries("remoteDatasets");
      queryClient.invalidateQueries("marketplacePersonas");
    },
  });
};

const syncAllRemoteResources = () => {
  // Unified sync: dispatches by resource_type on the backend — remote_mre hubs sync
  // datasets, litellm_gateway resources discover + create/sync their models.
  return axios.post(`${getBaseUrl()}/sync-all`).then((response) => {
    return response.data;
  });
};

const syncResourceModels = (id: number) => {
  return axios.post(`${getBaseUrl()}/${id}/sync-models`).then((response) => {
    return response.data;
  });
};

export const useSyncResourceModels = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => syncResourceModels(id),
    onSuccess: () => {
      queryClient.invalidateQueries("resource-list");
    },
  });
};

const syncResourceTools = (id: number) => {
  return axios.post(`${getBaseUrl()}/${id}/sync-tools`).then((response) => {
    return response.data;
  });
};

export const useSyncResourceTools = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: number) => syncResourceTools(id),
    onSuccess: () => {
      queryClient.invalidateQueries("resource-list");
      queryClient.invalidateQueries("toolkits");
    },
  });
};

export const useSyncAllRemoteResources = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: syncAllRemoteResources,
    onSuccess: () => {
      queryClient.invalidateQueries("resource-list");
      queryClient.invalidateQueries("remoteDatasets");
      queryClient.invalidateQueries("marketplacePersonas");
    },
  });
};
